import './NewRoulette.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  BottomBar,
  Button,
  Form,
  FormItem,
  Header,
  Input,
  List,
  ListItem,
  ScrollView,
} from '$uikit';
import { RouletteNewLot } from '$core/NewRoulette/RouletteNewLot/RouletteNewLot';
import { CurrencyEnum, Lot } from '$store/models';
import { classNames as cn, confirmSheet, statReachGoal } from '$utils';
import { useDispatch, useSelector } from 'react-redux';
import { attachActions } from '$store/attach';
import { Api } from '$api';
import { formatInputAmount } from '$utils/number';
import { IcDelete28, IcPlusCircle28 } from '$assets';
import { toastActions } from '$store/toast';
import { goBack } from '$navigation/helper';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { ColorsEnum } from '$shared/constants/colors';
import { rouletteActions, rouletteSelector } from '$store/roulette';

export const NewRoulette: FC = () => {
  const dispatch = useDispatch();

  const { lots, price, editLot } = useSelector(rouletteSelector);

  const user = useCurrentUserInfo();
  const t = useTranslator();

  const handleDeleteLot = useCallback(async (id: number) => {
    if (await confirmSheet('Вы действительно хотите удалить этот приз')) {
      dispatch(attachActions.clear(`roulette_lott_${id}`));
      dispatch(rouletteActions.deleteLot(id));
    }
  }, []);

  const handleCreateRoulette = useCallback(async () => {
    if (!(parseFloat(price) > 0)) {
      return dispatch(toastActions.setToastFail(t('roulette_create_error_empty_price')));
    }
    if (lots.length < 2) {
      return dispatch(toastActions.setToastFail(t('roulette_create_error_few_prizes')));
    }
    try {
      const data: { id: number } = await Api.post('/roulette/create', {
        price: parseFloat(price),
        lots,
        // postId: user?.postId,
      });
      dispatch(toastActions.hideToast());
      dispatch(
        attachActions.addRoulette({
          pickerId: `post_${user.id}`,
          rouletteId: data.id,
        }),
      );
      lots.forEach((lot) => dispatch(attachActions.clear(`roulette_lott_${lot.id}`)));
      statReachGoal('roulette_create_success');
      dispatch(rouletteActions.clear());
      goBack();
    } catch (e) {
      dispatch(toastActions.setToastFail(e.message));
    }
  }, [price, lots, t]);

  const notPopularColor = useMemo(() => {
    const colors = Object.values(ColorsEnum).reverse();

    const topColor = lots.reduce(
      (acc, lot) => {
        const color = lot.color;
        if (acc[color]) {
          acc[color]++;
        } else {
          acc[color] = 1;
        }
        return acc;
      },
      colors.reduce((acc, color) => ({ ...acc, [color]: 0 }), {}),
    );

    return Object.keys(topColor).reduce((a, b) => (topColor[a] < topColor[b] ? a : b));
  }, [lots]);

  return (
    <>
      <Header
        showBackButton={true}
        backButtonOnClick={
          editLot ? () => dispatch(rouletteActions.stopEdit()) : undefined
        }
      >
        {t(editLot ? 'new_roulette_add_lot_title' : 'new_roulette_title')}
      </Header>
      {editLot === null ? (
        <>
          <ScrollView>
            <Form>
              <FormItem title={t('roulette_price_one_spin')}>
                <Input
                  currency={CurrencyEnum.EUR}
                  type="price"
                  value={price}
                  onChange={(e) =>
                    dispatch(rouletteActions.setPrice(formatInputAmount(e.target.value)))
                  }
                />
              </FormItem>
              <FormItem title={t('roulette_prizes_title')}>
                <List>
                  {lots.map((lot) => (
                    <ListItem
                      key={lot.id}
                      onClick={() => dispatch(rouletteActions.editLot(lot.id))}
                      before={
                        <div className={cn('RouletteLotList__Lot__Icon', lot.color)} />
                      }
                      icon={
                        <div
                          className="RouletteLotList__Lot__deleteIcon"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await handleDeleteLot(lot.id);
                          }}
                        >
                          <IcDelete28 />
                        </div>
                      }
                    >
                      {lot.title}
                    </ListItem>
                  ))}
                  {lots.length < 20 && (
                    <ListItem
                      onClick={() => dispatch(rouletteActions.createNewLot())}
                      before={
                        <div className="RouletteLotList__Lot__Icon">
                          <IcPlusCircle28 />
                        </div>
                      }
                    >
                      {t('roulette_add_prize')}
                    </ListItem>
                  )}
                </List>
              </FormItem>
            </Form>
          </ScrollView>
          <BottomBar>
            <Button stretched onClick={handleCreateRoulette}>
              {t('roulette_create_button')}
            </Button>
          </BottomBar>
        </>
      ) : (
        <RouletteNewLot />
      )}
    </>
  );
};
